import { CoreQuery, Query } from "@gen/app-query";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useLocation, useRouter } from "@tanstack/react-router";
import {
  startTransition,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ClearableSelectWithPlaceholder } from "~/components/select_with_placeholder";
import { useActiveClient, useAuthOperations } from "~/contexts/auth_query";
import { useStableDebouncedCallback } from "~/utilities/timing";

import { ClientLogo } from "./ClientLogo";

interface ClientOption {
  id: string;
  name: string;
  baseUrl: string;
}

const VIEW_ROUTE_PREFIXES = [
  "/clients/$clientId",
  "/admin/clients/$clientId",
  "/admin",
];

interface ClientSwitcherProps {
  isCollapsed: boolean;
  onExpand: () => void;
  onClientChange?: () => void;
}

export function ClientSwitcher({
  isCollapsed,
  onExpand,
  onClientChange,
}: ClientSwitcherProps) {
  const { activeClientId, setActiveClientId, accessibleClientIds } =
    useActiveClient();
  const { canExecuteAnyAdminOperation } = useAuthOperations();
  const router = useRouter();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const isFirstRenderRef = useRef(true);

  // NOTE Non-admins rely on accessible client IDs fetched below, so they are filtered there.
  // Admin searches pass undefined as clientIdsFilter to query every client, so further filtering is redundant.
  const {
    data: adminData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isAdminLoading,
  } = useInfiniteQuery({
    queryKey: ["clients", debouncedQuery],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await CoreQuery.Client().admin_getClients(
        pageParam,
        50,
        undefined,
        undefined,
        debouncedQuery || undefined,
        undefined,
      );
      return res;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.length < 50) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
    enabled: canExecuteAnyAdminOperation(),
  });

  const shouldFetchAccessibleClients =
    !canExecuteAnyAdminOperation() && accessibleClientIds.length > 0;

  const accessibleClientQueries = useQueries({
    queries: shouldFetchAccessibleClients
      ? accessibleClientIds.map((clientId) => ({
          queryKey: Query.core_getClientQueryKey(clientId),
          queryFn: () => Query.Client().core_getClient(clientId),
        }))
      : [],
  });

  const isAccessibleClientsLoading =
    shouldFetchAccessibleClients &&
    accessibleClientQueries.some((q) => q.isLoading);

  const { data: adminActiveClientData } = CoreQuery.useAdmin_getClientQuery(
    activeClientId!,
    {
      enabled: Boolean(activeClientId) && canExecuteAnyAdminOperation(),
    },
  );

  const { data: coreActiveClientData } = Query.useCore_getClientQuery(
    activeClientId!,
    {
      enabled: Boolean(activeClientId) && !canExecuteAnyAdminOperation(),
    },
  );

  const activeClientData = canExecuteAnyAdminOperation()
    ? adminActiveClientData
    : coreActiveClientData;

  const debouncedSetQuery = useStableDebouncedCallback((query: string) => {
    startTransition(() => {
      setDebouncedQuery(query);
    });
  }, 300);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    debouncedSetQuery(searchQuery);
    return () => debouncedSetQuery.cancel();
  }, [searchQuery, debouncedSetQuery]);

  useEffect(() => {
    if (activeClientData?.name && !isOpen) {
      setSearchQuery(activeClientData.name);
    }
  }, [activeClientData?.name, isOpen]);

  const accessibleClientQueriesData = accessibleClientQueries.map(
    (q) => q.data,
  );

  const options: ClientOption[] = useMemo(() => {
    if (canExecuteAnyAdminOperation()) {
      const pages = adminData?.pages ?? [];
      const allClients = pages.flatMap((page) => page.data ?? []);
      const clients = allClients.map((client) => ({
        id: client.id ?? "",
        name: client.name ?? "",
        baseUrl: client.base_url ?? "",
      }));

      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase();
        return clients.filter(
          (c) =>
            c.name.toLowerCase().includes(lowerQuery) ||
            c.id.toLowerCase().includes(lowerQuery),
        );
      }

      return clients;
    }

    const clients: ClientOption[] = [];
    accessibleClientIds.forEach((id, index) => {
      const client = accessibleClientQueriesData[index];
      clients.push({
        id,
        name: client?.name || client?.id || id,
        baseUrl: client?.base_url || "",
      });
    });
    return clients;
  }, [
    canExecuteAnyAdminOperation,
    adminData?.pages,
    accessibleClientIds,
    accessibleClientQueriesData,
    searchQuery,
  ]);

  // The debounced server search and the immediate client-side filter above
  // operate on `debouncedQuery` and `searchQuery` respectively. Until they
  // match, `options` can still reflect a stale, in-flight search, so an empty
  // result isn't yet trustworthy as "found nothing".
  const isDebounceSettled = debouncedQuery === searchQuery;
  const hasFoundNothing =
    canExecuteAnyAdminOperation() &&
    isDebounceSettled &&
    !isAdminLoading &&
    !isFetchingNextPage &&
    debouncedQuery.length > 0 &&
    options.length === 0;

  const { data: idLookupClientData, isFetching: isIdLookupFetching } =
    CoreQuery.useAdmin_getClientQuery(debouncedQuery, {
      enabled: hasFoundNothing,
    });

  const displayOptions: ClientOption[] = useMemo(() => {
    if (
      hasFoundNothing &&
      idLookupClientData?.id &&
      !options.some((opt) => opt.id === idLookupClientData.id)
    ) {
      return [
        {
          id: idLookupClientData.id,
          name: idLookupClientData.name ?? "",
          baseUrl: idLookupClientData.base_url ?? "",
        },
      ];
    }
    return options;
  }, [hasFoundNothing, idLookupClientData, options]);

  const selectedValue = useMemo(() => {
    if (!activeClientId) {
      return null;
    }

    const optionMatch = options.find((opt) => opt.id === activeClientId);
    if (optionMatch) {
      return optionMatch;
    }

    if (activeClientData) {
      return {
        id: activeClientId,
        name: activeClientData.name ?? "",
        baseUrl: activeClientData.base_url ?? "",
      };
    }

    return null;
  }, [activeClientId, options, activeClientData]);

  const handleInputChange = useCallback(
    (_event: SyntheticEvent, value: string, reason: string) => {
      if (reason === "reset") {
        return;
      }
      setSearchQuery(value);
      if (value === "") {
        setDebouncedQuery("");
      }
    },
    [],
  );

  const switchClient = useCallback(
    (clientId: string | null) => {
      const { fullPath, params } = router.state.matches.at(-1)!;
      const prefix = VIEW_ROUTE_PREFIXES.find((p) => fullPath.startsWith(p));
      const viewPath = prefix ? fullPath.slice(prefix.length) : fullPath;
      const candidates = clientId
        ? [`/clients/$clientId${viewPath}`, "/clients/$clientId/transactions"]
        : [
            `/admin${viewPath}`,
            `/admin/clients/$clientId${viewPath}`,
            "/admin/transactions",
          ];

      setActiveClientId(clientId);
      router.navigate({
        to: candidates.find((path) => path in router.routesByPath)!,
        params: clientId ? { ...params, clientId } : params,
      });
      onClientChange?.();
    },
    [router, setActiveClientId, onClientChange],
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent, value: ClientOption | null) => {
      if (value) {
        setSearchQuery(value.name);
        setIsOpen(false);
        switchClient(value.id);
      } else {
        setSearchQuery("");
        switchClient(null);
      }
    },
    [switchClient],
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const clientId = event.target.value;
      if (clientId) {
        switchClient(clientId);
      }
    },
    [switchClient],
  );

  const handleExpandClick = () => {
    setIsOpen(true);
    onExpand();
  };

  const isLoading = canExecuteAnyAdminOperation()
    ? isAdminLoading || isFetchingNextPage
    : isAccessibleClientsLoading;

  const isAdmin = canExecuteAnyAdminOperation();

  if (isCollapsed) {
    if (!location.pathname.startsWith("/clients")) {
      return null;
    }

    return (
      <Tooltip title={selectedValue?.name || "Switch Client"} placement="right">
        <Box>
          <IconButton
            onClick={handleExpandClick}
            sx={{
              width: 40,
              height: 40,
              color: "common.white",
              bgcolor: "primary._states.hover",
              "&:hover": {
                bgcolor: "primary._states.selected",
              },
            }}
          >
            <ClientLogo
              clientId={selectedValue?.id ?? ""}
              alt={selectedValue?.name ?? ""}
              size={20}
              isAdmin={isAdmin}
              showBackground
            />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  if (!isAdmin) {
    const hasOnlyOneClient = accessibleClientIds.length <= 1;

    return (
      <FormControl fullWidth>
        <ClearableSelectWithPlaceholder
          placeholder="Choose Client"
          value={activeClientId ?? ""}
          onChange={handleSelectChange}
          isClearable={false}
          disabled={hasOnlyOneClient}
          startAdornment={
            <Stack direction="row" alignItems="center" sx={{ mr: 0.5 }}>
              <ClientLogo
                clientId={selectedValue?.id ?? ""}
                alt={selectedValue?.name ?? ""}
                size={20}
                isAdmin={false}
              />
            </Stack>
          }
          renderValue={(value) => {
            const client = options.find((opt) => opt.id === value);
            return client?.name ?? "";
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <ClientLogo
                  clientId={option.id}
                  alt={option.name}
                  size={20}
                  isAdmin={false}
                />
                <Typography noWrap variant="body2">
                  {option.name}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </ClearableSelectWithPlaceholder>
      </FormControl>
    );
  }

  return (
    <Autocomplete
      open={isOpen}
      onOpen={() => {
        setIsOpen(true);
        setDebouncedQuery(searchQuery);
      }}
      onClose={() => {
        setIsOpen(false);
        if (selectedValue) {
          setSearchQuery(selectedValue.name);
        }
      }}
      value={selectedValue}
      inputValue={searchQuery}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={displayOptions}
      loading={isLoading || isIdLookupFetching}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      slotProps={{
        listbox: {
          onScroll: (event: SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - 20
            ) {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }
          },
        },
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          data-testid={`client-option-${option.id}`}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <ClientLogo clientId={option.id} alt={option.name} size={20} />
            <Stack sx={{ minWidth: 0 }}>
              <Typography noWrap variant="body2">
                {option.name}
              </Typography>
            </Stack>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search clients..."
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              "data-testid": "client-switcher-input",
            },
            input: {
              ...params.InputProps,
              startAdornment: (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ ml: 1, mr: 0.5 }}
                >
                  <ClientLogo
                    clientId={selectedValue?.id ?? ""}
                    alt={selectedValue?.name ?? ""}
                    size={20}
                  />
                </Stack>
              ),
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      fullWidth
    />
  );
}
